import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { roleService } from '../../services/apiService';

const TeacherForm = ({ 
  open = false,
  onClose,
  initialData = {},
  onSubmit, 
  loading = false,
  title = 'Add New Teacher',
  externalErrors = {}
}) => {
  const [formData, setFormData] = useState({
    role_id: '',
    full_name: '',
    email: '',
    phone: '',
    date_of_joining: '',
    address: '',
    is_active: 1, // Use 1 for active, 0 for inactive
  });

  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState(null);

  // Fetch roles from API
  const fetchRoles = useCallback(async () => {
    try {
      setRolesLoading(true);
      setRolesError(null);
      const response = await roleService.getAll();
      
      if (response.success) {
        const rolesData = response.data || [];
        setRoles(rolesData);
      } else {
        setRolesError(response.error || 'Failed to fetch roles');
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      setRolesError(err.message || 'Failed to fetch roles');
    } finally {
      setRolesLoading(false);
    }
  }, [initialData?.id]);

  // Load roles when modal opens
  useEffect(() => {
    if (open) {
      fetchRoles();
    }
  }, [open, fetchRoles]);

  // Initialize form data when initialData changes or modal opens/closes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0 && initialData.id) {
      // Editing existing teacher - Convert is_active to number: true/1/'1' -> 1, false/0/'0' -> 0
      const isActiveValue = initialData.is_active !== undefined 
        ? (initialData.is_active === true || initialData.is_active === 1 || initialData.is_active === '1' ? 1 : 0)
        : 1;
      setFormData({
        role_id: initialData.role_id || '',
        full_name: initialData.full_name || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        date_of_joining: initialData.date_of_joining || '',
        address: initialData.address || '',
        is_active: isActiveValue,
      });
    } else {
      // Reset form for new teacher - always reset when modal opens without initialData
      setFormData({
        role_id: '',
        full_name: '',
        email: '',
        phone: '',
        date_of_joining: '',
        address: '',
        is_active: 1, // Default to active
      });
    }
    setErrors({});
  }, [initialData, open]);
  
  // Apply external errors (from API) to form
  useEffect(() => {
    if (Object.keys(externalErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...externalErrors }));
    }
  }, [externalErrors]);

  // Auto-select Teacher role when roles are loaded and this is a new teacher
  useEffect(() => {
    if (roles.length > 0 && !initialData?.id && !formData.role_id) {
      const teacherRole = roles.find(role => role.role_name === 'Teacher');
      if (teacherRole) {
        console.log('Auto-selecting teacher role after roles loaded:', teacherRole);
        setFormData(prev => ({
          ...prev,
          role_id: teacherRole.id
        }));
      }
    }
  }, [roles, initialData?.id, formData.role_id]);

  // Debug effect to log roles and form data changes
  useEffect(() => {
    if (roles.length > 0 && formData.role_id) {
      const selectedRole = roles.find(role => role.id === formData.role_id);
      console.log('Selected role:', selectedRole);
    }
  }, [roles, formData.role_id]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Role is auto-selected for new teachers, so only validate if editing
    if (initialData?.id && !formData.role_id.trim()) {
      newErrors.role_id = 'Role is required';
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      // Validate phone: must start with 9 or 6, exactly 10 digits
      const phoneRegex = /^[9876]\d{9}$/;
      if (!phoneRegex.test(formData.phone.trim())) {
        newErrors.phone = 'Phone number must start with 9,8,7 or 6 and be exactly 10 digits';
      }
    }

    if (!formData.date_of_joining.trim()) {
      newErrors.date_of_joining = 'Date of joining is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, initialData]);

  const handleSubmit = useCallback(() => {
    if (validateForm()) {
      // Ensure is_active is sent as number (1 or 0) to backend
      const dataToSubmit = {
        ...formData,
        is_active: formData.is_active === 1 || formData.is_active === true ? 1 : 0,
      };
      
      // Include ID when updating existing teacher
      if (initialData?.id) {
        dataToSubmit.id = initialData.id;
      }
      
      onSubmit(dataToSubmit);
    }
  }, [validateForm, onSubmit, formData, initialData?.id]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 400,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Fill in the details to {initialData?.id ? 'update' : 'create'} a teacher
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth error={!!errors.role_id}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData?.role_id || ''}
                onChange={handleChange('role_id')}
                label="Role"
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    if (rolesLoading) {
                      return 'Loading...';
                    }
                    return 'Teacher';
                  }
                  const selectedRole = roles.find(role => role.id === selected);
                  return selectedRole?.role_name || 'Teacher';
                }}
                disabled={loading || rolesLoading || !initialData?.id} // Auto-select and lock when creating
              >
                {rolesLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">Loading roles...</Typography>
                    </Box>
                  </MenuItem>
                ) : rolesError ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="error">
                      Error loading roles
                    </Typography>
                  </MenuItem>
                ) : roles && roles.length > 0 ? (
                  <>
                    <MenuItem value="">
                      <em>Select a role</em>
                    </MenuItem>
                    {roles?.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.role_name}
                      </MenuItem>
                    ))}
                  </>
                ) : (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No roles available
                    </Typography>
                  </MenuItem>
                )}
              </Select>
              {errors.role_id ? (
                <FormHelperText>{errors.role_id}</FormHelperText>
              ) : !initialData?.id ? (
                <FormHelperText>
                  Role is automatically set to "Teacher" for new teacher accounts
                </FormHelperText>
              ) : null}
            </FormControl>
            <TextField
              label="Full Name"
              value={formData.full_name}
              onChange={handleChange('full_name')}
              error={!!errors.full_name}
              helperText={errors.full_name}
              fullWidth
              required
              disabled={loading}
              placeholder="e.g., Dr. John Smith"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              required
              disabled={loading}
              placeholder="e.g., john.smith@school.com"
              type="email"
            />
            <TextField
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => {
                // Only allow digits, max 10 digits
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setFormData(prev => ({ ...prev, phone: value }));
                // Clear error when user starts typing
                if (errors.phone) {
                  setErrors(prev => ({ ...prev, phone: '' }));
                }
              }}
              error={!!errors.phone}
              helperText={errors.phone || 'Must start with 9,8,7 or 6, exactly 10 digits'}
              fullWidth
              required
              disabled={loading}
              placeholder="e.g., 9876543210"
              inputProps={{
                maxLength: 10,
                pattern: '[96]\\d{9}'
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Date of Joining"
              value={formData.date_of_joining}
              onChange={handleChange('date_of_joining')}
              error={!!errors.date_of_joining}
              helperText={errors.date_of_joining}
              fullWidth
              required
              disabled={loading}
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>

          {initialData?.id && (
            <TextField
              label="Address"
              value={formData.address || ''}
              onChange={(e) => {
                const value = e.target.value.slice(0, 200); // Limit to 200 characters
                setFormData(prev => ({ ...prev, address: value }));
                if (errors.address) {
                  setErrors(prev => ({ ...prev, address: '' }));
                }
              }}
              error={!!errors.address}
              helperText={errors.address || `${(formData.address || '').length}/200 characters`}
              fullWidth
              disabled={loading}
              multiline
              rows={3}
              placeholder="Enter teacher's address"
              inputProps={{
                maxLength: 200
              }}
            />
          )}

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.is_active ? 1 : 0}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 1 ? 1 : 0 }))}
              label="Status"
              disabled={loading || !!initialData?.id}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
            {initialData?.id && (
              <FormHelperText>
                Status can only be changed using the Activate/Deactivate button on the Teachers Management page.
              </FormHelperText>
            )}
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'Saving...' : (initialData?.id ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeacherForm;
